import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallGroupedMultiSelect,
  type CurecallGroupedMultiSelectGroup,
  type CurecallMultiSelectItem,
} from '@honestica/curecall-ui'
import type {
  EHumanReviewRequestProblem,
  EHumanReviewResolution,
  THumanReviewTab,
} from './human-review-requests.type'
import { getAvailableProblems, getAvailableResolutions } from './human-review-filters.util'
import { useProblemLabels } from './use-problem-labels'
import { useResolutionLabels } from './use-resolution-labels'

const PROBLEM_PREFIX = 'problem:'
const RESOLUTION_PREFIX = 'resolution:'

type Props = {
  activeTab: THumanReviewTab
  medEventGroups: CurecallGroupedMultiSelectGroup[]
  allMedEventItems: CurecallMultiSelectItem<number>[]
  selectedProblems: EHumanReviewRequestProblem[]
  selectedResolutions: EHumanReviewResolution[]
  selectedMedEventIds: number[]
  onSelectStatuses: (statuses: {
    problems: EHumanReviewRequestProblem[]
    resolutions: EHumanReviewResolution[]
  }) => void
  onSelectMedEvents: (medEventIds: number[]) => void
}

const HumanReviewFilters = ({
  activeTab,
  medEventGroups,
  allMedEventItems,
  selectedProblems,
  selectedResolutions,
  selectedMedEventIds,
  onSelectStatuses,
  onSelectMedEvents,
}: Props) => {
  const { t } = useTranslation()
  const problemLabels = useProblemLabels()
  const resolutionLabels = useResolutionLabels()

  const availableProblems = useMemo(() => getAvailableProblems(activeTab), [activeTab])
  const availableResolutions = useMemo(() => getAvailableResolutions(activeTab), [activeTab])

  const statusGroups = useMemo<CurecallGroupedMultiSelectGroup[]>(
    () => [
      {
        label: t('pages.dashboard.human-review-requests.filters.patients-to-contact'),
        value: 'problems',
        children: availableProblems.map((problem) => ({
          value: `${PROBLEM_PREFIX}${problem}`,
          label: `${problemLabels[problem]} (${activeTab.problemCounts[problem]})`,
        })),
      },
      {
        label: t('pages.dashboard.human-review-requests.filters.processed-patients'),
        value: 'resolutions',
        children: availableResolutions.map((resolution) => ({
          value: `${RESOLUTION_PREFIX}${resolution}`,
          label: `${resolutionLabels[resolution]} (${activeTab.resolutionCounts[resolution]})`,
        })),
      },
    ],
    [activeTab, availableProblems, availableResolutions, problemLabels, resolutionLabels, t],
  )

  const selectedStatusItems = useMemo<CurecallMultiSelectItem<string>[]>(
    () => [
      ...selectedProblems.map((problem) => ({
        value: `${PROBLEM_PREFIX}${problem}`,
        label: problemLabels[problem],
      })),
      ...selectedResolutions.map((resolution) => ({
        value: `${RESOLUTION_PREFIX}${resolution}`,
        label: resolutionLabels[resolution],
      })),
    ],
    [selectedProblems, selectedResolutions, problemLabels, resolutionLabels],
  )

  const selectedMedEventItems = useMemo(
    () => allMedEventItems.filter((item) => selectedMedEventIds.includes(item.value)),
    [allMedEventItems, selectedMedEventIds],
  )

  const handleStatusSelect = (items: CurecallMultiSelectItem<string>[]) => {
    const selectedValues = new Set(items.map((item) => item.value))
    onSelectStatuses({
      problems: availableProblems.filter((problem) => selectedValues.has(`${PROBLEM_PREFIX}${problem}`)),
      resolutions: availableResolutions.filter((resolution) =>
        selectedValues.has(`${RESOLUTION_PREFIX}${resolution}`),
      ),
    })
  }

  const totalFilterCount = selectedProblems.length + selectedResolutions.length
  const totalResultCount =
    selectedProblems.reduce((sum, problem) => sum + (activeTab.problemCounts[problem] ?? 0), 0) +
    selectedResolutions.reduce((sum, resolution) => sum + (activeTab.resolutionCounts[resolution] ?? 0), 0)

  return (
    <div className="c-flex c-items-end c-gap-4 c-py-2">
      <div className="c-w-[350px]">
        <CurecallGroupedMultiSelect
          placeholder={t('pages.dashboard.human-review-requests.filters.patients-to-contact')}
          groups={statusGroups}
          selectedItems={selectedStatusItems}
          selectedElementsLabel={t('pages.dashboard.human-review-requests.filters.active-filters-label', {
            filterCount: totalFilterCount,
            resultCount: totalResultCount,
          })}
          onSelect={handleStatusSelect}
          searchPlaceholder={t('pages.dashboard.human-review-requests.filters.search-placeholder')}
          noResultText={t('pages.dashboard.human-review-requests.filters.no-result')}
          clearSearchAriaLabel={t('pages.dashboard.human-review-requests.filters.clear-search')}
          openOptionsAriaLabel={t('pages.dashboard.human-review-requests.filters.open-options')}
          closeOptionsAriaLabel={t('pages.dashboard.human-review-requests.filters.close-options')}
          inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1"
          optionsClassName="!c-max-h-80"
          showSummaryTooltip={false}
        />
      </div>
      {allMedEventItems.length > 1 && (
        <div className="c-w-[350px]">
          <CurecallGroupedMultiSelect
            placeholder={t('pages.dashboard.human-review-requests.filters.choose-a-medevent')}
            groups={medEventGroups}
            selectedItems={selectedMedEventItems}
            selectedElementsLabel={t(
              'pages.dashboard.human-review-requests.filters.filter-selected-medevents-label',
              { count: selectedMedEventIds.length },
            )}
            onSelect={(items: CurecallMultiSelectItem<number>[]) =>
              onSelectMedEvents(items.map((item) => item.value))
            }
            searchPlaceholder={t('pages.dashboard.human-review-requests.filters.search-placeholder')}
            noResultText={t('pages.dashboard.human-review-requests.filters.no-result')}
            clearSearchAriaLabel={t('pages.dashboard.human-review-requests.filters.clear-search')}
            openOptionsAriaLabel={t('pages.dashboard.human-review-requests.filters.open-options')}
            closeOptionsAriaLabel={t('pages.dashboard.human-review-requests.filters.close-options')}
            inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1"
            optionsClassName="!c-max-h-80"
            showSummaryTooltip={false}
          />
        </div>
      )}
    </div>
  )
}

export default HumanReviewFilters
